'use client';
import React, { useState } from 'react';
import { CalendarDays, Clock, MapPin, CheckCircle, XCircle, RotateCcw, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

// Backend integration point: GET /api/workers/me/booking-requests
const bookingRequests = [
  {
    id: 'booking-req-001',
    employerLabel: 'Employer #B412',
    serviceType: 'Elder care — full day',
    date: '06 May 2026',
    time: '07:00–17:00',
    suburb: 'Waterkloof Ridge',
    status: 'requested',
    notes: 'My mother has moderate Alzheimer\'s. She uses a walking frame.',
    requestedAt: '2 hours ago',
  },
  {
    id: 'booking-req-002',
    employerLabel: 'Employer #C089',
    serviceType: 'Companionship visit',
    date: '07 May 2026',
    time: '09:00–13:00',
    suburb: 'Lynnwood',
    status: 'accepted',
    notes: 'Weekly companionship for my father.',
    requestedAt: '1 day ago',
  },
  {
    id: 'booking-req-003',
    employerLabel: 'Employer #A237',
    serviceType: 'Overnight care',
    date: '10 May 2026',
    time: '18:00–07:00',
    suburb: 'Centurion',
    status: 'requested',
    notes: 'Post-op recovery support needed.',
    requestedAt: '3 hours ago',
  },
  {
    id: 'booking-req-004',
    employerLabel: 'Employer #D551',
    serviceType: 'Medication reminder check',
    date: '05 May 2026',
    time: '08:00–09:00',
    suburb: 'Faerie Glen',
    status: 'confirmed',
    notes: '',
    requestedAt: '3 days ago',
  },
];

const statusConfig: Record<string, { label: string; bg: string; color: string }> = {
  requested: { label: 'Requested', bg: '#EEF2FF', color: '#4F46E5' },
  accepted: { label: 'Accepted', bg: 'var(--success-bg)', color: 'var(--success)' },
  confirmed: { label: 'Confirmed', bg: 'var(--orange-pale)', color: 'var(--primary)' },
  declined: { label: 'Declined', bg: 'var(--danger-bg)', color: 'var(--danger)' },
};

export default function WorkerBookingRequests() {
  const [requests, setRequests] = useState(bookingRequests);
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const filters = ['all', 'requested', 'accepted', 'confirmed'];
  const filtered = activeFilter === 'all' ? requests : requests.filter((r) => r.status === activeFilter);

  const handleAccept = (id: string) => {
    setRequests((prev) => prev.map((r) => r.id === id ? { ...r, status: 'accepted' } : r));
    toast.success('Booking accepted', { description: 'The employer will be notified to confirm and pay.' });
  };

  const handleDecline = (id: string) => {
    setRequests((prev) => prev.map((r) => r.id === id ? { ...r, status: 'declined' } : r));
    toast.error('Booking declined', { description: 'The employer has been notified.' });
  };

  return (
    <div className="card overflow-hidden">
      <div className="px-5 pt-5 pb-0">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold" style={{ color: 'var(--navy)' }}>Booking requests</h3>
          <span className="text-xs px-2 py-1 rounded-full font-semibold" style={{ background: 'var(--orange-pale)', color: 'var(--primary)' }}>
            {requests.filter((r) => r.status === 'requested').length} pending
          </span>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 border-b border-border pb-0 -mx-5 px-5 overflow-x-auto">
          {filters.map((f) => (
            <button
              key={`req-filter-${f}`}
              onClick={() => setActiveFilter(f)}
              className="px-3 py-2 text-xs font-semibold capitalize whitespace-nowrap transition-all duration-150 relative border-b-2"
              style={{
                borderColor: activeFilter === f ? 'var(--primary)' : 'transparent',
                color: activeFilter === f ? 'var(--primary)' : 'var(--muted-foreground)',
              }}
            >
              {f === 'all' ? `All (${requests.length})` : `${f.charAt(0).toUpperCase() + f.slice(1)} (${requests.filter((r) => r.status === f).length})`}
            </button>
          ))}
        </div>
      </div>

      <div className="divide-y divide-border">
        {filtered.length === 0 ? (
          <div className="p-8 text-center">
            <CalendarDays size={32} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
            <p className="text-sm font-semibold mb-1" style={{ color: 'var(--navy)' }}>No {activeFilter !== 'all' ? activeFilter : ''} bookings</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>When employers request your time, you&apos;ll see them here.</p>
          </div>
        ) : (
          filtered.map((req) => {
            const sc = statusConfig[req.status] ?? statusConfig.requested;
            return (
              <div key={req.id} className="p-5 hover:bg-muted/30 transition-colors">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-sm" style={{ color: 'var(--navy)' }}>{req.serviceType}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: sc.bg, color: sc.color }}>
                        {sc.label}
                      </span>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{req.employerLabel} · {req.requestedAt}</p>
                  </div>
                  <ChevronRight size={16} style={{ color: 'var(--border)' }} className="flex-shrink-0 mt-1" />
                </div>

                <div className="flex flex-wrap gap-3 text-xs mb-3" style={{ color: 'var(--muted-foreground)' }}>
                  <span className="flex items-center gap-1"><CalendarDays size={12} />{req.date}</span>
                  <span className="flex items-center gap-1"><Clock size={12} />{req.time}</span>
                  <span className="flex items-center gap-1"><MapPin size={12} />{req.suburb}</span>
                </div>

                {req.notes && (
                  <p className="text-xs italic mb-3 px-3 py-2 rounded-lg" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                    &ldquo;{req.notes}&rdquo;
                  </p>
                )}

                {req.status === 'requested' && (
                  <div className="flex gap-2">
                    <button onClick={() => handleAccept(req.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all duration-150 active:scale-95" style={{ background: 'var(--success-bg)', color: 'var(--success)' }}>
                      <CheckCircle size={14} /> Accept
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all duration-150 active:scale-95" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                      <RotateCcw size={14} /> Propose new time
                    </button>
                    <button onClick={() => handleDecline(req.id)} className="flex items-center justify-center gap-1 py-2 px-3 rounded-xl text-xs font-bold transition-all duration-150 active:scale-95" style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}>
                      <XCircle size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}